import React from "react";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";

const COMPANY = {
  name: "Pendli AS",
  email: "hello@pendli.no",
  site: "www.pendli.no",
  address: "Oppgis ved formell selskapsregistrering",
  orgNr: "Under registrering",
  updated: "24. juni 2026",
};

const DESCRIPTION_NO =
  "Pendli kobler pendlere som trenger parkering nær togstasjoner med naboer som har en oppkjørsel som står tom. Vi gjør det enkelt å forhåndsbooke en garantert parkeringsplass — og enkelt for huseiere å tjene på en plass som ellers ikke brukes.";

export default function Personvern() {
  const { t, lang } = useLang();
  useSeo({ title: t.seo.personvern.title, description: t.seo.personvern.description, path: "/personvern", lang });

  return (
    <main data-testid="personvern-page" className="bg-ink text-cream pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <span className="text-xs font-bold tracking-widest uppercase text-amber">Juridisk</span>
        <h1 className="mt-4 font-display text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
          Personvernerklæring
        </h1>
        <p className="mt-4 text-cream/50 text-sm">Sist oppdatert: {COMPANY.updated}</p>

        <p className="mt-10 text-base md:text-lg text-cream/80 leading-relaxed">
          Pendli tar personvern på alvor. Denne erklæringen forklarer hvilke opplysninger vi samler inn, hvorfor vi gjør det, og hvilke rettigheter du har.
        </p>

        <Section title="1. Hvilke opplysninger samler vi inn?">
          <p>Vi kan samle inn følgende informasjon:</p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-cream/75">
            <li>Navn</li>
            <li>E-postadresse</li>
            <li>Informasjon du sender inn via kontaktskjema eller venteliste</li>
            <li>Tekniske data som IP-adresse, nettlesertype og bruksmønster på nettsiden</li>
          </ul>
        </Section>

        <Section title="2. Hva bruker vi opplysningene til?">
          <p>Vi bruker opplysningene til å:</p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-cream/75">
            <li>Levere og forbedre Pendli</li>
            <li>Besvare henvendelser</li>
            <li>Sende informasjon om produktet dersom du har samtykket til dette</li>
            <li>Analysere bruk av nettsiden</li>
          </ul>
        </Section>

        <Section title="3. Deling av opplysninger">
          <p>
            Vi selger aldri personopplysninger. Opplysninger kan deles med leverandører som hjelper oss med drift av nettsiden, analyse og kommunikasjon, men kun i den grad det er nødvendig.
          </p>
        </Section>

        <Section title="4. Lagring">
          <p>
            Vi lagrer personopplysninger så lenge det er nødvendig for formålet de ble samlet inn for, eller så lenge loven krever.
          </p>
        </Section>

        <Section title="5. Dine rettigheter">
          <p>Du har rett til å:</p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-cream/75">
            <li>Få innsyn i hvilke opplysninger vi har om deg</li>
            <li>Be om retting eller sletting</li>
            <li>Trekke tilbake samtykke</li>
            <li>Klage til Datatilsynet</li>
          </ul>
        </Section>

        <Section title="6. Kontakt">
          <p>
            For spørsmål om personvern, kontakt oss på{" "}
            <a className="amber-link" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
          </p>
        </Section>

        <ContactBlock />
      </div>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12 border-t border-amber/15 pt-8">
      <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight mb-4" style={{ letterSpacing: "-0.02em" }}>
        {title}
      </h2>
      <div className="text-base md:text-lg text-cream/75 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function ContactBlock() {
  return (
    <section className="mt-14 border border-amber/30 bg-amber-deep/40 p-7 md:p-9 relative l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br">
      <h3 className="text-xs font-bold tracking-widest uppercase text-amber mb-4">Kontaktinformasjon</h3>
      <dl className="text-sm md:text-base space-y-2 text-cream/80">
        <div className="flex flex-col sm:flex-row sm:gap-3">
          <dt className="sm:w-44 text-cream/50">Selskap</dt>
          <dd className="font-medium">{COMPANY.name}</dd>
        </div>
        <div className="flex flex-col sm:flex-row sm:gap-3">
          <dt className="sm:w-44 text-cream/50">E-post</dt>
          <dd>
            <a className="amber-link" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
          </dd>
        </div>
        <div className="flex flex-col sm:flex-row sm:gap-3">
          <dt className="sm:w-44 text-cream/50">Nettside</dt>
          <dd>{COMPANY.site}</dd>
        </div>
        <div className="flex flex-col sm:flex-row sm:gap-3">
          <dt className="sm:w-44 text-cream/50">Adresse</dt>
          <dd className="text-cream/60">{COMPANY.address}</dd>
        </div>
        <div className="flex flex-col sm:flex-row sm:gap-3">
          <dt className="sm:w-44 text-cream/50">Organisasjonsnummer</dt>
          <dd className="text-cream/60">{COMPANY.orgNr}</dd>
        </div>
      </dl>
      <p className="mt-6 text-xs text-cream/40">{DESCRIPTION_NO}</p>
    </section>
  );
}

export { COMPANY, DESCRIPTION_NO, Section, ContactBlock };
